import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet, apiPost } from "../lib/api";
import { COLORS } from "../lib/colors";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatMessageTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export default function ChatTab({ onUnreadCount }) {
  const { candidate, session } = useAuth();

  // Conversation list state
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);

  // Active chat state
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [msgDraft, setMsgDraft] = useState("");
  const [sending, setSending] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const pollRef = useRef(null);
  const convoPollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ---- Conversation list ----

  const loadConversations = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const data = await apiGet("/student/conversations", session.access_token);
      setConversations(data.conversations || []);
      const totalUnread = (data.conversations || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
      onUnreadCount?.(totalUnread);
    } catch (err) {
      console.error("Failed to load conversations:", err);
    } finally {
      setLoadingConvos(false);
    }
  }, [session?.access_token, onUnreadCount]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Poll conversation list every 10s when viewing list
  useEffect(() => {
    if (selectedRecruiter) return;
    convoPollRef.current = setInterval(loadConversations, 10000);
    return () => clearInterval(convoPollRef.current);
  }, [selectedRecruiter, loadConversations]);

  // ---- Chat thread ----

  const fetchMessages = useCallback(async (recruiterId, since) => {
    if (!session?.access_token) return;
    try {
      const path = since
        ? `/student/messages/${recruiterId}?since=${encodeURIComponent(since)}`
        : `/student/messages/${recruiterId}`;
      const data = await apiGet(path, session.access_token);
      if (since) {
        const newMsgs = (data.messages || []).filter(
          (m) => !m._optimistic
        );
        if (newMsgs.length > 0) {
          setMessages((prev) => {
            // Remove optimistic messages that now have real versions
            const filtered = prev.filter((p) => !p._optimistic);
            const existingIds = new Set(filtered.map((m) => m.id));
            const truly_new = newMsgs.filter((m) => !existingIds.has(m.id));
            return [...filtered, ...truly_new];
          });
        }
      } else {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  }, [session?.access_token]);

  const openChat = useCallback(async (convo) => {
    setSelectedRecruiter(convo);
    setMessages([]);
    setLoadingMessages(true);
    setMsgDraft("");
    await fetchMessages(convo.recruiter_id);
    setLoadingMessages(false);
    // Refresh conversations to update unread counts
    loadConversations();
  }, [fetchMessages, loadConversations]);

  // Poll messages every 5s when in chat
  useEffect(() => {
    if (!selectedRecruiter) return;
    pollRef.current = setInterval(() => {
      const nonOptimistic = messages.filter((m) => !m._optimistic);
      const last = nonOptimistic[nonOptimistic.length - 1];
      if (last?.created_at) {
        fetchMessages(selectedRecruiter.recruiter_id, last.created_at);
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [selectedRecruiter, messages, fetchMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (selectedRecruiter) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedRecruiter]);

  const sendMessage = async () => {
    const text = msgDraft.trim();
    if (!text || !selectedRecruiter || sending) return;

    setSending(true);
    setMsgDraft("");

    // Optimistic update
    const optimistic = {
      id: `optimistic-${Date.now()}`,
      sender_role: "student",
      text,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      await apiPost("/student/messages", {
        recruiter_id: selectedRecruiter.recruiter_id,
        text,
      }, session.access_token);
    } catch (err) {
      console.error("Failed to send message:", err);
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setMsgDraft(text); // Restore draft
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const searchMessages = async () => {
    const q = searchQuery.trim();
    if (!q || q.length < 2 || !session?.access_token) return;
    setSearching(true);
    try {
      const data = await apiGet(`/student/messages/search?q=${encodeURIComponent(q)}`, session.access_token);
      setSearchResults(data.results || []);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
  };

  const goBack = () => {
    setSelectedRecruiter(null);
    setMessages([]);
    clearInterval(pollRef.current);
    loadConversations();
  };

  // ---- Render: Chat thread view ----

  if (selectedRecruiter) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 20px",
          borderBottom: `1px solid ${COLORS.borderLight}`,
          backgroundColor: COLORS.surface,
        }}>
          <button
            onClick={goBack}
            style={{
              background: "none", border: "none", fontSize: 20,
              color: COLORS.accent, cursor: "pointer", padding: 0, lineHeight: 1,
            }}
          >
            &#8592;
          </button>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: COLORS.surfaceAlt,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: COLORS.textSecondary, flexShrink: 0,
          }}>
            {(selectedRecruiter.recruiter_company || "R").substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, display: "flex", alignItems: "center", gap: 6 }}>
              {selectedRecruiter.recruiter_name}
              {selectedRecruiter.recruiter_status === "revoked" && (
                <span style={{
                  fontSize: 10, fontWeight: 600, color: COLORS.textMuted,
                  backgroundColor: COLORS.surfaceAlt, padding: "1px 6px",
                  borderRadius: 4, lineHeight: "16px",
                }}>
                  Inactive
                </span>
              )}
            </div>
            {selectedRecruiter.recruiter_company && (
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                {selectedRecruiter.recruiter_company}
              </div>
            )}
          </div>
        </div>

        {/* Messages area */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "16px 20px",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {loadingMessages ? (
            <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 20 }}>
              Loading...
            </p>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 20px" }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>&#128172;</div>
              <p style={{ fontSize: 13, color: COLORS.textSecondary }}>
                No messages yet. Say hello!
              </p>
            </div>
          ) : (
            messages.map((m, idx) => {
              const isStudent = m.sender_role === "student";
              const nextMsg = messages[idx + 1];
              const isLastInGroup = !nextMsg || nextMsg.sender_role !== m.sender_role;
              return (
                <div
                  key={m.id}
                  style={{
                    alignSelf: isStudent ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                  }}
                >
                  <div style={{
                    padding: "10px 14px",
                    borderRadius: 16,
                    borderBottomRightRadius: isStudent ? 4 : 16,
                    borderBottomLeftRadius: isStudent ? 16 : 4,
                    backgroundColor: isStudent ? COLORS.accent : COLORS.surfaceAlt,
                    color: isStudent ? "#fff" : COLORS.text,
                    fontSize: 14,
                    lineHeight: 1.4,
                    opacity: m._optimistic ? 0.7 : 1,
                    wordBreak: "break-word",
                  }}>
                    {m.text}
                  </div>
                  <div style={{
                    fontSize: 10,
                    color: COLORS.textMuted,
                    marginTop: 3,
                    textAlign: isStudent ? "right" : "left",
                    paddingLeft: isStudent ? 0 : 4,
                    paddingRight: isStudent ? 4 : 0,
                  }}>
                    {formatMessageTime(m.created_at)}
                    {isStudent && isLastInGroup && !m._optimistic && (
                      <span style={{ marginLeft: 6, color: m.read_at ? COLORS.accent : COLORS.textMuted }}>
                        {m.read_at ? "\u2713 Read" : "\u2713 Sent"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        {selectedRecruiter.recruiter_status === "revoked" ? (
          <div style={{
            padding: "14px 20px",
            borderTop: `1px solid ${COLORS.borderLight}`,
            backgroundColor: COLORS.surface,
            textAlign: "center",
          }}>
            <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
              This recruiter is no longer active on the platform
            </p>
          </div>
        ) : (
          <div style={{
            display: "flex", gap: 8, padding: "12px 20px",
            borderTop: `1px solid ${COLORS.borderLight}`,
            backgroundColor: COLORS.surface,
          }}>
            <input
              ref={inputRef}
              type="text"
              value={msgDraft}
              onChange={(e) => setMsgDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 20,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.bg,
                fontSize: 14, fontFamily: "inherit",
                outline: "none",
                color: COLORS.text,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!msgDraft.trim() || sending}
              style={{
                width: 40, height: 40, borderRadius: "50%",
                backgroundColor: msgDraft.trim() ? COLORS.accent : COLORS.surfaceAlt,
                border: "none", cursor: msgDraft.trim() ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, color: msgDraft.trim() ? "#fff" : COLORS.textMuted,
                flexShrink: 0, transition: "background-color 0.15s",
              }}
            >
              &#9654;
            </button>
          </div>
        )}
      </div>
    );
  }

  // ---- Render: Conversation list view ----

  return (
    <div>
      {/* Header */}
      <div style={{ padding: "20px 20px 4px" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text }}>
          Messages
        </h2>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>
          Chat with recruiters
        </p>
      </div>

      {/* Search bar */}
      <div style={{ padding: "10px 20px 0" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value) clearSearch(); }}
            onKeyDown={(e) => e.key === "Enter" && searchMessages()}
            placeholder="Search messages..."
            style={{
              flex: 1, padding: "9px 14px", borderRadius: 20,
              border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg,
              fontSize: 13, fontFamily: "inherit", outline: "none", color: COLORS.text,
            }}
          />
          {searchQuery && (
            <button onClick={clearSearch} style={{
              background: "none", border: "none", fontSize: 16, color: COLORS.textMuted,
              cursor: "pointer", padding: "0 4px",
            }}>&#10005;</button>
          )}
        </div>
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div style={{ padding: "10px 20px" }}>
          <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} found
          </p>
          {searchResults.map((r) => (
            <button
              key={r.id}
              onClick={() => {
                const convo = conversations.find((c) => c.recruiter_id === r.recruiter_id);
                if (convo) { clearSearch(); openChat(convo); }
              }}
              style={{
                width: "100%", backgroundColor: COLORS.surface,
                border: `1px solid ${COLORS.borderLight}`, borderRadius: 10,
                padding: 12, marginBottom: 8, cursor: "pointer",
                textAlign: "left", fontFamily: "inherit",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.accent }}>
                  {r.recruiter_name || "Recruiter"}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textMuted }}>
                  {formatTime(r.created_at)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.4 }}>
                {r.sender_role === "student" ? "You: " : ""}{r.text?.substring(0, 100)}{r.text?.length > 100 ? "..." : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {searching && (
        <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 12 }}>
          Searching...
        </p>
      )}

      {/* Conversation list */}
      {searchResults.length === 0 && !searching && (
      <div style={{ padding: "14px 20px" }}>
        {loadingConvos ? (
          <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 20 }}>
            Loading...
          </p>
        ) : conversations.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>&#128172;</div>
            <p style={{ fontSize: 13, color: COLORS.textSecondary }}>
              No messages yet. When a recruiter contacts you, conversations will appear here.
            </p>
          </div>
        ) : (
          conversations.map((convo) => {
            const logo = (convo.recruiter_company || "R").substring(0, 2).toUpperCase();
            return (
              <button
                key={convo.recruiter_id}
                onClick={() => openChat(convo)}
                style={{
                  width: "100%",
                  backgroundColor: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 10,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  backgroundColor: COLORS.surfaceAlt,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700, color: COLORS.textSecondary, flexShrink: 0,
                }}>
                  {logo}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                    <span style={{
                      fontSize: 15, fontWeight: 600, color: COLORS.text,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      {convo.recruiter_name}
                      {convo.recruiter_status === "revoked" && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: COLORS.textMuted,
                          backgroundColor: COLORS.surfaceAlt, padding: "1px 6px",
                          borderRadius: 4, lineHeight: "16px", flexShrink: 0,
                        }}>
                          Inactive
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 11, color: COLORS.textMuted, flexShrink: 0, marginLeft: 8 }}>
                      {formatTime(convo.last_message_at)}
                    </span>
                  </div>
                  {convo.recruiter_company && (
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 2 }}>
                      {convo.recruiter_company}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{
                      fontSize: 13, color: COLORS.textSecondary,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      flex: 1,
                    }}>
                      {convo.last_sender === "student" ? "You: " : ""}
                      {convo.last_message}
                    </span>
                    {convo.unread_count > 0 && (
                      <span style={{
                        minWidth: 20, height: 20, borderRadius: 10,
                        backgroundColor: COLORS.accent,
                        color: "#fff", fontSize: 11, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "0 6px", flexShrink: 0, marginLeft: 8,
                      }}>
                        {convo.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      )}
    </div>
  );
}
